import { Property } from 'csstype';
import React, { PropsWithChildren } from 'react';
import { useStrip } from '../hooks/useStrip';
import { HANDLE_SIZE } from '../utils/graph.utils';
import { Box, BoxProps, forwardRef } from '@cdl/heartbeat-react';

interface StripSquareProps extends BoxProps, PropsWithChildren {
  'data-testid'?: string;
  onset: number;
  offset: number;
  main?: React.SVGProps<SVGRectElement>;
  leftHandle?: React.SVGProps<SVGRectElement>;
  rightHandle?: React.SVGProps<SVGRectElement>;
  handleWidth?: number;
}

export const StripSquare = forwardRef<StripSquareProps, 'div'>((props, ref) => {
  const {
    main,
    leftHandle,
    rightHandle,
    onset,
    offset,
    cursor,
    handleWidth = HANDLE_SIZE,
    children,
    ...rest
  } = props;

  const { xScale, height } = useStrip();

  const x = xScale(onset);
  const width = xScale(offset) - xScale(onset);

  return (
    <Box as='g' className='strip-square' ref={ref} {...rest}>
      {children && <g transform={`translate(${x})`}>{children}</g>}
      {main && (
        <rect
          tabIndex={-1}
          data-testid={`${props['data-testid']}-main`}
          x={x}
          width={width}
          height={height}
          {...main}
          style={{
            cursor: (cursor ?? 'grab') as Property.Cursor,
            outline: 'none',
            ...main.style,
          }}
        />
      )}
      {leftHandle && (
        <rect
          tabIndex={-1}
          data-testid={`${props['data-testid']}-start`}
          fill='transparent'
          x={x - handleWidth / 2}
          width={handleWidth}
          height={height}
          {...leftHandle}
          style={{
            ...leftHandle.style,
            cursor: (cursor ?? 'w-resize') as Property.Cursor,
            outline: 'none',
          }}
        />
      )}
      {rightHandle && (
        <rect
          tabIndex={-1}
          data-testid={`${props['data-testid']}-end`}
          fill='transparent'
          x={x + width - handleWidth / 2}
          width={handleWidth}
          height={height}
          {...rightHandle}
          style={{
            ...rightHandle.style,
            cursor: (cursor ?? 'e-resize') as Property.Cursor,
            outline: 'none',
          }}
        />
      )}
    </Box>
  );
});
